import { useState, useEffect, useCallback } from "react";
import { Loader2, ArrowLeft, ClipboardList, ListChecks, FileText, Plus, Pencil, TrendingUp, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import ProgramForm from "@/components/admin/ProgramForm";
import LetterForm from "@/components/admin/LetterForm";
import { STATUS_BADGE, STATUS_LABEL, PLAN_LABEL, ACTIVITY_LABELS, formatDate } from "@/components/admin/patientConstants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PatientDetail = ({ patient, exercises, subscribers, onBack, onListChanged }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProgramForm, setShowProgramForm] = useState(false);
  const [showLetterForm, setShowLetterForm] = useState(false);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/patients/${encodeURIComponent(patient.email)}`, { withCredentials: true });
      setDetail(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [patient.email]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const refreshAll = () => {
    fetchDetail();
    onListChanged();
  };

  const handleSendLetter = async (letterId) => {
    try {
      await axios.post(`${API}/letters/${letterId}/send`, {}, { withCredentials: true });
      toast.success("Lettera inviata via email");
      fetchDetail();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'invio");
    }
  };

  const q = detail?.questionnaire;
  const program = detail?.program;
  const letters = detail?.letters || [];
  const progress = detail?.progress || [];
  const maxCount = Math.max(...progress.map((w) => w.count), 1);
  const activeSub = detail?.subscriptions?.find((s) => s.status === "active");
  const programFormInitial = program || { patient_email: patient.email, patient_name: patient.name };
  const letterFormInitial = { patient_email: patient.email, patient_name: patient.name };

  return (
    <div data-testid="patient-detail">
      <button
        onClick={onBack}
        className="inline-flex items-center gap-2 text-[#8a9a86] hover:text-[#2c4c3b] transition-colors mb-6 text-sm font-medium"
        data-testid="back-to-patients-btn"
      >
        <ArrowLeft className="w-4 h-4" />
        Torna alla lista pazienti
      </button>

      <div className="bg-white rounded-3xl p-6 sm:p-8 border border-stone-100 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="font-serif text-2xl text-stone-900">{patient.name || "—"}</h2>
            <p className="text-sm text-stone-500">{patient.email}</p>
            <p className="text-xs text-stone-400 mt-1">Iscritto: {formatDate(patient.registered_at)}</p>
          </div>
          <div className="text-left sm:text-right">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${STATUS_BADGE[patient.subscription_status] || STATUS_BADGE.none}`}>
              {STATUS_LABEL[patient.subscription_status] || patient.subscription_status}
            </span>
            {activeSub && (
              <p className="text-xs text-stone-500 mt-1.5">Piano: {PLAN_LABEL[activeSub.plan_id] || activeSub.plan_id}</p>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#2c4c3b]" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Questionario */}
          <div className="bg-white rounded-3xl p-6 sm:p-8 border border-stone-100" data-testid="patient-questionnaire-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#e8ece6] flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-[#2c4c3b]" />
              </div>
              <h3 className="font-serif text-xl text-stone-900">Questionario Iniziale</h3>
            </div>
            {q ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div className="bg-[#f5f2ec] rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Tipo di dolore</p>
                  <p className="text-stone-700">{q.pain_type}</p>
                </div>
                <div className="bg-[#f5f2ec] rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Obiettivo</p>
                  <p className="text-stone-700">{q.goal}</p>
                </div>
                <div className="bg-[#f5f2ec] rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Attrezzatura a casa</p>
                  <p className="text-stone-700">{q.equipment}</p>
                </div>
                <div className="bg-[#f5f2ec] rounded-xl p-4">
                  <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Attività fisica</p>
                  <p className="text-stone-700">{ACTIVITY_LABELS[q.activity_level] || q.activity_level}</p>
                </div>
                {q.extra_notes && (
                  <div className="bg-[#f5f2ec] rounded-xl p-4 sm:col-span-2">
                    <p className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">Note</p>
                    <p className="text-stone-700">{q.extra_notes}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-stone-500">Questionario non ancora compilato.</p>
            )}
          </div>

          {/* Scheda esercizi */}
          <div className="bg-white rounded-3xl p-6 sm:p-8 border border-stone-100" data-testid="patient-program-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#e8ece6] flex items-center justify-center">
                  <ListChecks className="w-5 h-5 text-[#2c4c3b]" />
                </div>
                <h3 className="font-serif text-xl text-stone-900">Scheda Esercizi</h3>
              </div>
              <Button
                onClick={() => setShowProgramForm(true)}
                size="sm"
                className="bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50 rounded-full"
                data-testid="patient-edit-program-btn"
              >
                {program ? (<><Pencil className="w-4 h-4 mr-1.5" />Modifica Scheda</>) : (<><Plus className="w-4 h-4 mr-1.5" />Crea Scheda</>)}
              </Button>
            </div>
            {program && program.items?.length > 0 ? (
              <div className="space-y-2">
                {program.items.map((item) => (
                  <div key={item.exercise_id} className="flex flex-wrap items-center justify-between gap-2 bg-[#f5f2ec] rounded-xl px-4 py-3 text-sm">
                    <span className="font-medium text-stone-800">
                      {item.exercise ? `${item.exercise.code} — ${item.exercise.title}` : item.exercise_id}
                    </span>
                    <span className="text-xs text-stone-500">
                      {item.sets} serie × {item.reps} · {item.weekly_frequency}x/sett. · rec. {item.rest_time}
                      {program.completions?.[item.exercise_id] && <span className="ml-2 text-[#2c4c3b] font-medium">✓ completato</span>}
                    </span>
                  </div>
                ))}
                {program.notes && <p className="text-sm text-stone-500 italic mt-2">Note: {program.notes}</p>}
              </div>
            ) : (
              <p className="text-sm text-stone-500">Nessuna scheda assegnata.</p>
            )}
          </div>

          {/* Storico Progressi */}
          <div className="bg-white rounded-3xl p-6 sm:p-8 border border-stone-100" data-testid="patient-progress-section">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-[#e8ece6] flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#2c4c3b]" />
              </div>
              <h3 className="font-serif text-xl text-stone-900">Storico Progressi</h3>
            </div>
            {progress.some((w) => w.count > 0) ? (
              <>
                <div className="flex items-end gap-2 h-36">
                  {progress.map((w) => (
                    <div key={w.week_start} className="flex-1 flex flex-col items-center justify-end gap-1 h-full">
                      <span className="text-xs font-semibold text-[#2c4c3b]">{w.count > 0 ? w.count : ""}</span>
                      <div
                        className="w-full max-w-[36px] bg-[#2c4c3b] rounded-t-lg transition-all"
                        style={{
                          height: `${(w.count / maxCount) * 80}%`,
                          minHeight: w.count > 0 ? "10px" : "3px",
                          opacity: w.count > 0 ? 1 : 0.12,
                        }}
                      />
                      <span className="text-[9px] text-stone-400 whitespace-nowrap">
                        {new Date(w.week_start).toLocaleDateString("it-IT", { day: "numeric", month: "short" })}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-stone-400 mt-4">Esercizi completati per settimana (ultime 8 settimane)</p>
              </>
            ) : (
              <p className="text-sm text-stone-500">Nessun esercizio completato nelle ultime 8 settimane.</p>
            )}
          </div>

          {/* Lettere */}
          <div className="bg-white rounded-3xl p-6 sm:p-8 border border-stone-100" data-testid="patient-letters-section">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#e8ece6] flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#2c4c3b]" />
                </div>
                <h3 className="font-serif text-xl text-stone-900">Lettere Personalizzate</h3>
              </div>
              <Button
                onClick={() => setShowLetterForm(true)}
                size="sm"
                className="bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50 rounded-full"
                data-testid="patient-new-letter-btn"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                Nuova Lettera
              </Button>
            </div>
            {letters.length > 0 ? (
              <div className="space-y-2">
                {letters.map((letter) => (
                  <div key={letter.id} className="flex flex-wrap items-center justify-between gap-2 bg-[#f5f2ec] rounded-xl px-4 py-3 text-sm">
                    <div>
                      <span className="font-medium text-stone-800">{letter.title}</span>
                      <span className="text-xs text-stone-500 ml-2">{letter.month}</span>
                      {letter.last_sent_at && (
                        <p className="text-[11px] text-[#8a9a86] mt-0.5">
                          Inviata il {new Date(letter.last_sent_at).toLocaleDateString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSendLetter(letter.id)}
                      className="rounded-full border-[#2c4c3b]/30 text-[#2c4c3b] hover:bg-[#e8ece6]"
                      data-testid={`send-letter-${letter.id}`}
                    >
                      <Send className="w-3.5 h-3.5 mr-1.5" />
                      {letter.last_sent_at ? "Reinvia" : "Invia email"}
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-stone-500">Nessuna lettera per questo paziente.</p>
            )}
          </div>
        </div>
      )}

      {showProgramForm && (
        <ProgramForm
          initial={programFormInitial}
          exercises={exercises}
          subscribers={subscribers}
          onClose={() => setShowProgramForm(false)}
          onSaved={() => {
            setShowProgramForm(false);
            refreshAll();
          }}
        />
      )}
      {showLetterForm && (
        <LetterForm
          initial={letterFormInitial}
          onClose={() => setShowLetterForm(false)}
          onSaved={() => {
            setShowLetterForm(false);
            refreshAll();
          }}
        />
      )}
    </div>
  );
};

export default PatientDetail;
